// @flow
import React, {useEffect, useState, useCallback, Fragment, useRef} from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {useLocation, useHistory} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import {
  Grid,
  Typography,
  Hidden,
  MenuItem,
  List,
  ListItem,
  useMediaQuery,
  Divider,
  IconButton,
  Chip,
  Tooltip,
  Snackbar,
  Tabs,
  Tab,
  Collapse,
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import Skeleton from '@material-ui/lab/Skeleton';
import AlertDueDateTier from '../../tiers/components/AlertDueDateTier/AlertDueDateTier';

import {colors} from '../../../assets/styles/colors';

import CustomButton from '../../../elements/CustomButton/CustomButton';
import {
  AddCircleOutline,
  Close,
  InfoOutlined,
  PlayCircleFilledTwoTone,
  TuneRounded,
} from '@material-ui/icons';

import SaveAltRoundedIcon from '@material-ui/icons/SaveAltRounded';
import CustomSelect from '../../../elements/CustomSelect/CustomSelect';
import OrderItem from './OrderItem';
import {useIsFeatureInTier} from '../../../utils/component';

import dayjs from 'dayjs';
import 'dayjs/locale/es';
import relativeTime from 'dayjs/plugin/relativeTime';
import TierRestrictionModal from '../../../elements/TierRestrictionModal/TierRestrictionModal';
import DownloadModal from '../../../elements/DownloadModal/DownloadModal';

import {usePage} from '../../page/utils';

import {
  ORDER_STATES,
  ORDER_STATE_COLORS,
  ORDER_STATE_FILTER_TOOLTIPS,
  ORDER_STATE_STRINGS,
} from '../../../entities';
import type {
  Order,
  User,
  Store,
  Currency,
  OrderState,
  Pagination as PaginationType,
  Collaborator,
  OrderStateMessageConfig,
} from '../../../entities';
import StateChangeInfoModal from './StateChangeInfoModal';
import PremiumFeatureBadge from '../../../elements/PremiumFeatureBadge/PremiumFeatureBadge';
import StateChangeMessageModal from './StateChangeMessageModal';
import preOrderSteps from '../../../assets/img/preOrderSteps.png';
import preOrderSteps2 from '../../../assets/img/preOrderSteps2.png';
import HorizontalFilter from '../../../elements/HorizontalFilter/HorizontalFilter';
import HorizontalScroll from '../../../elements/HorizontalFilter/HorizontalScroll/HorizontalScroll';
import FilterButton from '../../../elements/HorizontalFilter/FilterButton/FilterButton';
import SnackBar from '../../../elements/SnackBar/SnackBar';
import SearchInput from '../../../elements/SearchInput';
import CustomHorizontalScroll from '../../../elements/CustomHorizontalScroll';
import {TIENDADA_LINKS} from '../../../constants';
import {useStoreStockSetting} from '../../configure/sales-process/hooks/useStoreStockSetting';

dayjs.locale('es');
dayjs.extend(relativeTime);

type Props = {
  fetchOrders: ({
    queryFilter?: {[key: string]: string | typeof undefined},
  }) => void,
  fetchPreOrders: ({queryFilter?: {[key: string]: string}}) => void,
  orders: Order[],
  pagination?: PaginationType,
  queryFilter?: Object,
  isFetching: boolean,
  store?: Store,
  currency?: Currency,
  user?: User,
  selectedStates: OrderState[],
  toggleSelectedState: (state: OrderState | 'ALL' | 'PRE_ORDERS') => void,
  updateSelectedStates: (states: any[]) => void,
  resetFilters: () => void,
  updateOrderStateSuccess?: Order,
  updateOrderStateError: any,
  order: Order,
  orderStateMessages?: OrderStateMessageConfig[],
  getOrderStateMessages: () => void,
  resetOrderState: () => void,
  fetchCollaborators: ({collaboratorsRequest: Object}) => void,
  fetchCollaboratorInfo: () => void,
  collaborators?: Collaborator[],
  updateOrderCollaboratorSuccess: boolean,
  tempCollaborators: boolean,
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    // height: '100%',
    background: '#f8f9fb',
  },
  // title: {
  //   marginTop: theme.spacing(3),
  // },
  navBackground: {
    [theme.breakpoints.down('sm')]: {
      background: 'white !important',
    },
  },
  toolbar: theme.mixins.toolbar,
  headerWrapper: {
    padding: '1rem',
    background: '#f8f9fb',
    [theme.breakpoints.down('sm')]: {
      background: 'white',
      position: 'sticky',
      top: 72,
      zIndex: 3,
      padding: theme.spacing(1, 4),
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    },
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
    [theme.breakpoints.down('xs')]: {
      fontSize: '18px',
    },
  },
  titleCategories: {
    fontSize: '1rem',
    fontWeight: 600,
  },
  downloadReportButton: {
    marginRight: theme.spacing(2),
  },
  addOrderButton: {
    marginRight: theme.spacing(2),
    padding: '4px 13px',
  },
  tutorialsButton: {
    // zIndex: 3,
    backgroundColor: 'white',
    color: colors.purple3,
    padding: '4px 13px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    [theme.breakpoints.down('sm')]: {
      backgroundColor: 'unset',
      color: colors.purple3,
      paddingLeft: 5,
      boxShadow: 'unset',
    },
  },
  mejorarPlanButton: {
    marginLeft: 4,
    backgroundColor: 'white',
    color: colors.purple3,
    padding: '4px 13px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  mobileFiltersButton: {
    color: colors.purple3,
  },
  mobileFiltersButtonActive: {
    '&:hover': {
      backgroundColor: colors.lightPurple,
    },
    backgroundColor: colors.lightPurple,
  },
  alert: {
    marginBottom: theme.spacing(2),
    // [theme.breakpoints.down('sm')]: {
    //   marginBottom: theme.spacing(1),
    // },
  },
  content: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    // height: '100%',
    // overflowY: 'auto',
    overflowX: 'hidden',
    padding: '0 .5rem',
    marginBottom: 64,
    [theme.breakpoints.down('sm')]: {
      // overflowY: 'auto',
      marginBottom: 180,
    },
    [theme.breakpoints.down('xs')]: {
      marginBottom: 220,
    },
    [theme.breakpoints.up('md')]: {
      padding: '0 1rem',
    },
  },
  ordersContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    // overflow: 'auto',
    borderRadius: '12px',
    background: 'white',
    maxWidth: 1400,
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    // paddingTop: theme.spacing(3),
    // [theme.breakpoints.down('xs')]: {
    //   padding: theme.spacing(2),
    // },
    [theme.breakpoints.down('sm')]: {
      marginTop: 'unset',
      background: 'unset',
      boxShadow: 'unset',
    },
  },
  filterWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  filterLeft: {
    padding: theme.spacing(2),
    // fontSize: '0.875rem',
  },
  filterRight: {
    marginRight: '1rem',
  },
  filterButton: {
    color: colors.grey600,
    fontWeight: 'normal',
    borderRadius: '8px',
    padding: '4px 16px',
    // marginRight: theme.spacing(1),
  },
  filterButtonActive: {
    color: colors.purple3,
    backgroundColor: colors.lightPurple,
    fontWeight: 600,
  },
  filterTooltip: {
    borderRadius: '6px',
    background: colors.grey300,
    color: colors.grey800,
  },
  filterTooltipArrow: {
    color: colors.grey300,
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 3),
    gap: '2px',
    backgroundColor: colors.grey200,
  },
  headerColumn: {
    // flex: '2 1',
    width: '14%',
    fontSize: '0.875rem',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  orderList: {
    paddingTop: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  sectionHeader: {
    margin: '12px 0 4px 0',
    '&:first-letter': {
      textTransform: 'uppercase',
    },
  },
  listItem: {
    backgroundColor: 'transparent',
    padding: 0,
    '&:hover': {
      background: colors.grey200,
    },
  },
  divider: {
    margin: theme.spacing(0, 3),
    background: colors.grey200,
  },
  dividerVertical: {
    margin: theme.spacing(0, 1),
    background: colors.grey300,
  },
  noOrders: {
    textAlign: 'center',
    color: colors.grey700,
    margin: theme.spacing(2),
  },
  paginationArea: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  pagination: {
    margin: theme.spacing(2, 0),
    '& .Mui-selected': {
      backgroundColor: colors.lightPurple,
      color: colors.purple3,
    },
    '& .Mui-selected:hover': {
      backgroundColor: colors.lightPurple,
      // color: colors.purple3,
    },
  },
  sizeSelect: {
    margin: theme.spacing(2, 0),
    border: 'none',
    [theme.breakpoints.down('xs')]: {
      marginTop: 0,
    },
  },
  blur: {
    filter: 'blur(2px)',
  },
  blocker: {
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
    backgroundColor: 'transparent',
    zIndex: 2,
  },
  closeButton: {
    color: 'black',
    position: 'absolute',
    top: 0,
    right: 0,
  },
  upsellMessage: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(1),
    },
    maxWidth: 670,
    borderRadius: '12px',
    boxShadow:
      '0px 11px 80px rgba(96, 96, 96, 0.07), 0px 4.01518px 29.2013px rgba(96, 96, 96, 0.0482987), 0px 1.9493px 14.1767px rgba(96, 96, 96, 0.0389404), 0px 0.955581px 6.94968px rgba(96, 96, 96, 0.0310596), 0px 0.377838px 2.74791px rgba(96, 96, 96, 0.0217013)',
    display: 'flex',
    alignItems: 'center',
    color: colors.purple3,
    background: colors.lightPurple,
    fontSize: '0.875rem',
  },
  upsellMessage2: {
    padding: theme.spacing(2),
    margin: theme.spacing(2, 4),
    [theme.breakpoints.down('sm')]: {
      position: 'fixed',
      bottom: 64,
      left: 0,
      margin: theme.spacing(2, 4),
    },
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(2),
    },
    borderRadius: '12px',
    boxShadow:
      '0px 11px 80px rgba(96, 96, 96, 0.07), 0px 4.01518px 29.2013px rgba(96, 96, 96, 0.0482987), 0px 1.9493px 14.1767px rgba(96, 96, 96, 0.0389404), 0px 0.955581px 6.94968px rgba(96, 96, 96, 0.0310596), 0px 0.377838px 2.74791px rgba(96, 96, 96, 0.0217013)',
    display: 'flex',
    alignItems: 'center',
    color: colors.purple3,
    background: colors.lightPurple,
    fontSize: '0.875rem',
  },
  upsellText: {
    marginLeft: theme.spacing(1),
    color: colors.grey700,
    fontSize: '0.875rem',
  },
  newLabel: {
    background: '#F5F0FF',
    marginLeft: 10,
    fontWeight: 600,
    '& span': {
      color: '#7B39FF',
    },
  },
  modalDownloadWrapper: {
    maxWidth: 670,
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow:
      '0px 11px 80px rgba(96, 96, 96, 0.07), 0px 4.01518px 29.2013px rgba(96, 96, 96, 0.0482987), 0px 1.9493px 14.1767px rgba(96, 96, 96, 0.0389404), 0px 0.955581px 6.94968px rgba(96, 96, 96, 0.0310596), 0px 0.377838px 2.74791px rgba(96, 96, 96, 0.0217013)',
    padding: theme.spacing(5, 4, 3, 4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  stateChangeAlert: {
    width: '100%',
    borderRadius: '12px',
    background: 'white',
    boxShadow: '0px 4px 30px rgba(139, 139, 139, 0.25)',
    padding: theme.spacing(2, 0, 1, 2),
    paddingRight: theme.spacing(6),
    display: 'flex',
    flexWrap: 'wrap',
  },
  stateChangeAlertTitle: {
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(2),
    fontWeight: 600,
  },
  alertStatePill: {
    marginBottom: theme.spacing(1),
    // width: 96,
    color: 'white',
    fontSize: '0.875rem',
    borderRadius: '12px',
    padding: '2px 13px',
    textAlign: 'center',
  },
  mobileTabsDivider: {
    margin: theme.spacing(0, -2),
    width: `calc(100% + ${theme.spacing(2)}px)`,
    height: 1,
    background: colors.grey300,
  },
  customTab: {
    textTransform: 'none',
  },
  noPreOrdersContainer: {
    margin: theme.spacing(0, 3),
    background: 'white',
    borderRadius: '12px',
    border: `1px solid ${colors.grey300}`,
    padding: theme.spacing(0, 3, 3, 3),
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(2, 0),
    },
  },
  noPreOrdersTitle: {
    marginTop: theme.spacing(3),
    fontWeight: 600,
  },
  noPreOrdersText: {
    marginTop: theme.spacing(1),
    color: colors.grey700,
  },
  searchInput: {
    background: colors.grey300,
    color: colors.grey600,
    width: '100%',
  },
  tooltip: {
    marginLeft: theme.spacing(2),
    backgroundColor: '#F4F4F6',
    borderRadius: '8px',
    textAlign: 'center',
    maxWidth: 240,
  },
  tableScrollContainer: {
    width: '100%',

    [theme.breakpoints.up('md')]: {
      minWidth: 1300,
    },
  },
  tableScrollArrowButton: {
    background: 'white',
    padding: '.75rem',
    boxShadow: '0px 4px 30px rgba(139, 139, 139, 0.25)',
  },
}));

export default usePage<Props>(Orders, 'Orders');

function Orders(props: Props) {
  const {
    fetchOrders,
    fetchPreOrders,
    orders,
    pagination,
    queryFilter,
    isFetching,
    store,
    // currency,
    selectedStates,
    toggleSelectedState,
    updateSelectedStates,
    updateOrderStateSuccess,
    // updateOrderStateError,
    updateOrderCollaboratorSuccess,
    orderStateMessages,
    getOrderStateMessages,
    resetOrderState,
    fetchCollaborators,
    fetchCollaboratorInfo,
    tempCollaborators,
    // collaborators,
  } = props;

  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const {settings: stockSettings} = useStoreStockSetting();

  const [tierRestrictedModal, setTierRestrictedModal] = useState(false);

  const hasOrderManagementDashboardFeature = useIsFeatureInTier(
    'ORDER_MANAGEMENT_DASHBOARD'
  );

  const hasTaxInvoiceFeature = useIsFeatureInTier('TAX_INVOICE');

  const firstRender = useRef(true);
  const mobile = useMediaQuery('(max-width:960px)');

  const [tutorialModalOpen, setTutorialModalOpen] = useState(false);

  const getSearchFromQuery = (search: string) => {
    const s = queryString.parse(search);
    return s;
  };
  const query = getSearchFromQuery(location.search);
  // const {orderState} = queryString.parse(location.search);
  const p = parseInt(query.page, 10);
  const s = parseInt(query.size, 10);
  const statesQuery = query.states;
  const [page, setPage] = useState(
    isNaN(p) ? (pagination ? pagination.page + 1 : 1) : p
  );
  const [size, setSize] = useState(
    isNaN(s) ? (pagination ? pagination.size : 16) : s
  );

  const handlePageChange = (event, value) => {
    setPage(value);
  };
  const handleSizeChange = event => {
    if (event) {
      setSize(event.target.value);
    }
  };

  const buildQueryParamsCallback = useCallback(() => {
    const params: {[key: string]: any} = {
      size: size,
      page: page - 1,
    };
    const queryString = getSearchFromQuery(location.search);
    if (queryString) {
      params.name = queryString.name;
    }
    if (selectedStates && selectedStates.length > 0) {
      params.states = selectedStates.toString();
    }
    if (statesQuery) params.states = statesQuery;
    return params;
  }, [size, page, location.search, selectedStates, statesQuery]);

  useEffect(() => {
    if (typeof statesQuery !== 'string') return;

    if (statesQuery.includes('PRE_ORDERS')) {
      updateSelectedStates(['PRE_ORDERS']);
      return;
    }

    const states = statesQuery.split(',');

    const statesSelected = states.filter(state => ORDER_STATES.includes(state));
    updateSelectedStates(statesSelected);
  }, [statesQuery, updateSelectedStates]);

  // useEffect(() => {
  //   if (mobile && (!selectedStates || selectedStates[0] !== 'PRE_ORDERS')) {
  //     const mobileStates = [...ORDER_STATES];
  //     mobileStates.pop();
  //     if (mobileStates) updateSelectedStates(mobileStates);
  //   }
  // }, [mobile, selectedStates, updateSelectedStates]);

  const [prevQueryFilter, setPrevQueryFilter] = useState(queryFilter);

  useEffect(() => {
    const queryFilter = buildQueryParamsCallback();
    if (
      (!orders ||
        JSON.stringify(prevQueryFilter) !== JSON.stringify(queryFilter)) &&
      selectedStates
    ) {
      const isPreOrders =
        selectedStates[0] === 'PRE_ORDERS' ||
        queryFilter.states === 'PRE_ORDERS';

      if (isPreOrders) {
        fetchPreOrders({
          queryFilter: {
            states: 'CREATED',
            size: queryFilter.size ?? '16',
            page: queryFilter.page ?? '0',
          },
        });
      } else {
        fetchOrders({queryFilter});
      }
      setPrevQueryFilter(queryFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildQueryParamsCallback]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (!selectedStates) return;
    const params = new URLSearchParams({states: selectedStates.join(',')});
    history.replace({search: params.toString()});
  }, [history, selectedStates]);

  const handleStateOption = (state: OrderState | 'ALL' | 'PRE_ORDERS') => {
    if (state === 'PRE_ORDERS') history.replace({search: 'states=PRE_ORDERS'});
    else toggleSelectedState(state);
    // else history.replace({search: ''});
  };

  const handleStateOptionMobile = (
    state: OrderState | 'ALL' | 'PRE_ORDERS'
  ) => {
    if (selectedStates.includes(state)) {
      updateSelectedStates(['PENDING']);
    } else {
      updateSelectedStates([state]);
    }
  };

  const [stateChangeAlertOpen, setStateChangeAlertOpen] = useState(false);
  const [
    stateChangeMessageModalOpen,
    setStateChangeMessageModalOpen,
  ] = useState(false);
  const handleChangeMessageModalClose = useCallback(() => {
    setStateChangeMessageModalOpen(false);
  }, []);

  const hasOrderStateChangeMessageFeature = useIsFeatureInTier(
    'ORDER_STATE_CHANGE_MESSAGES'
  );

  useEffect(() => {
    if (hasOrderStateChangeMessageFeature && !orderStateMessages) {
      getOrderStateMessages();
    }
  }, [
    getOrderStateMessages,
    hasOrderStateChangeMessageFeature,
    orderStateMessages,
  ]);

  const getOrderStateIndex = (state: OrderState) => {
    return ORDER_STATES.findIndex(s => s === state);
  };

  useEffect(() => {
    if (updateOrderStateSuccess) {
      setStateChangeAlertOpen(true);
      if (hasOrderStateChangeMessageFeature && orderStateMessages) {
        if (
          updateOrderStateSuccess.previousState &&
          getOrderStateIndex(updateOrderStateSuccess.previousState) <
            getOrderStateIndex(updateOrderStateSuccess.orderState)
        ) {
          const stateMessageConfig = orderStateMessages.find(
            config => config.orderState === updateOrderStateSuccess.orderState
          );
          if (stateMessageConfig && stateMessageConfig.enabled)
            setStateChangeMessageModalOpen(true);
        }
      }
    } else {
      setStateChangeAlertOpen(false);
      setStateChangeMessageModalOpen(false);
    }
  }, [
    hasOrderStateChangeMessageFeature,
    orderStateMessages,
    updateOrderStateSuccess,
  ]);

  const handleSearchOrderSubmit = (value: string) => {
    if (!queryFilter) return;

    if (!value && !queryFilter.customerName) return;

    if (!value && queryFilter.customerName) {
      fetchOrders({
        queryFilter: {
          ...queryFilter,
          customerName: undefined,
        },
      });
      return;
    }

    fetchOrders({
      queryFilter: {
        ...queryFilter,
        customerName: value,
      },
    });
  };

  const paginationArea = (
    <div className={classes.paginationArea}>
      <Pagination
        count={
          pagination &&
          (pagination.size >= pagination.total
            ? 1
            : Math.ceil(pagination.total / pagination.size))
        }
        page={page}
        className={classes.pagination}
        color="primary"
        onChange={handlePageChange}
      />
      <Hidden smUp>
        <CustomSelect
          native
          pagination
          disableUnderline
          className={classes.sizeSelect}
          value={size}
          onChange={handleSizeChange}
        >
          {/* <option aria-label="None" value={size} /> */}
          {/* <option value={4}>4 por página</option> */}
          <option value={8}>8 por página</option>
          <option value={16}>16 por página</option>
          <option value={24}>24 por página</option>
          <option value={32}>32 por página</option>
        </CustomSelect>
      </Hidden>
      <Hidden xsDown>
        <CustomSelect
          pagination
          disableUnderline
          className={classes.sizeSelect}
          value={size}
          onChange={handleSizeChange}
        >
          {/* <option aria-label="None" value={size} /> */}
          {/* <MenuItem value={4}>4 por página</MenuItem> */}
          <MenuItem value={8}>8 por página</MenuItem>
          <MenuItem value={16}>16 por página</MenuItem>
          <MenuItem value={24}>24 por página</MenuItem>
          <MenuItem value={32}>32 por página</MenuItem>
        </CustomSelect>
      </Hidden>
    </div>
  );

  useEffect(
    () => () => {
      // resetFilters();
      resetOrderState();
    },
    [resetOrderState]
  );
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(true);

  const hasOrderExportFeature = useIsFeatureInTier('ORDER_EXPORT');

  const [isModalDownloadOpen, setIsModalDownloadOpen] = useState(false);
  const handleDownload = () => {
    if (!hasOrderExportFeature) setTierRestrictedModal(true);
    else setIsModalDownloadOpen(true);
  };

  const downloadModal = (
    <div className={classes.modalDownloadWrapper}>
      <DownloadModal
        open={isModalDownloadOpen}
        statesSelected={selectedStates}
        setStatesSelected={updateSelectedStates}
        handleClose={() => setIsModalDownloadOpen(false)}
      />
    </div>
  );

  const upsellSection = !hasOrderManagementDashboardFeature && (
    <>
      <Hidden mdUp>
        <div className={classes.title}>
          <div style={{display: 'flex', alignItems: 'center'}}>
            Mis Pedidos
            {!hasOrderManagementDashboardFeature && ' - Últimos 7 días'}
          </div>
        </div>
      </Hidden>
      <Typography style={{marginTop: '4px', fontSize: '0.875rem'}}>
        <Hidden smDown>
          Te mostramos tus pedidos de los <strong>últimos 7 días</strong>. Día a
          día se irán actualizando.{' '}
        </Hidden>
        Recuerda que los pedidos también te pueden llegar por WhatsApp. Sin
        embargo, te recomendamos que siempre revises esta vista.
      </Typography>
      <div
        style={{
          marginTop: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography style={{color: colors.grey600, fontSize: '0.875rem'}}>
          Para hacer uso de este módulo a la totalidad por favor mejorar tu
          plan.
        </Typography>
        <CustomButton
          className={classes.mejorarPlanButton}
          variant="contained"
          color="default"
          onClick={() => setTierRestrictedModal(true)}
        >
          Mejorar a TiendaDa PRO
        </CustomButton>
      </div>
    </>
  );

  // const canFilterOrders = useIsFeatureInTier('ORDER_FILTER');
  const canFilterOrders = true;
  const hasManualOrderCreationFeature = useIsFeatureInTier(
    'MANUAL_ORDER_CREATION'
  );

  const hasPreOrderFeature = useIsFeatureInTier('PRE_ORDER');

  const showPreOrders = selectedStates && selectedStates[0] === 'PRE_ORDERS';
  const handleChangeTab = (event, newValue) => {
    switch (newValue) {
      case 0:
        handleStateOptionMobile('PENDING');
        break;
      case 1:
        handleStateOptionMobile('PRE_ORDERS');
        break;
      default:
        break;
    }
  };

  const userHasOrderEditCollaboratorFeature = useIsFeatureInTier(
    'ORDER_EDIT_COLLABORATOR'
  );

  useEffect(() => {
    if (userHasOrderEditCollaboratorFeature)
      fetchCollaborators({collaboratorsRequest: {archived: false}});
  }, [fetchCollaborators, userHasOrderEditCollaboratorFeature]);

  return (
    <div className={classes.root}>
      <SnackBar
        mode="success"
        message={updateOrderCollaboratorSuccess ? 'Colaborador asignado.' : ''}
      />
      {updateOrderStateSuccess && updateOrderStateSuccess.orderState && (
        <Snackbar
          open={stateChangeAlertOpen}
          anchorOrigin={
            !mobile
              ? {
                  vertical: 'bottom',
                  horizontal: 'left',
                }
              : {
                  vertical: 'top',
                  horizontal: 'left',
                }
          }
          autoHideDuration={6000}
          onClose={() => setStateChangeAlertOpen(false)}
        >
          <div className={classes.stateChangeAlert}>
            <InfoOutlined />
            <Typography className={classes.stateChangeAlertTitle}>
              Cambio de estado exitoso
            </Typography>
            <div
              className={classes.alertStatePill}
              style={{
                backgroundColor: updateOrderStateSuccess.orderState
                  ? ORDER_STATE_COLORS[updateOrderStateSuccess.orderState].main
                  : undefined,
              }}
            >
              {ORDER_STATE_STRINGS[updateOrderStateSuccess.orderState]}
            </div>
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              style={{position: 'absolute', top: 8, right: 8}}
              onClick={() => {
                setStateChangeAlertOpen(false);
              }}
            >
              <Close fontSize="inherit" />
            </IconButton>
          </div>
        </Snackbar>
      )}
      {updateOrderStateSuccess &&
        hasOrderStateChangeMessageFeature &&
        updateOrderStateSuccess.type === 'order' && (
          <StateChangeMessageModal
            open={stateChangeMessageModalOpen}
            order={updateOrderStateSuccess}
            onClose={handleChangeMessageModalClose}
          />
        )}
      <TierRestrictionModal
        open={tierRestrictedModal}
        handleClose={() => setTierRestrictedModal(false)}
      />
      <AlertDueDateTier />

      <div className={classes.headerWrapper}>
        <Grid container justify="space-between" alignItems="center" spacing={2}>
          <Hidden smDown>
            <Grid item xs={12} md={3}>
              <div className={classes.title}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Mis Pedidos
                  {!hasOrderManagementDashboardFeature && ' - Últimos 7 días'}
                  {/* {!hasOrderManagementDashboardFeature && (
                    <StarRounded style={{marginLeft: 16, color: '#FFC107'}} />
                  )} */}
                  {!hasOrderManagementDashboardFeature && (
                    /*<StarRounded style={{marginLeft: 12, color: '#FFC107'}} />*/
                    <Chip
                      label="PRO"
                      size="small"
                      className={classes.newLabel}
                    />
                  )}
                  <IconButton onClick={() => setTutorialModalOpen(true)}>
                    <InfoOutlined />
                  </IconButton>
                </div>
              </div>
            </Grid>
          </Hidden>
          <Grid
            item
            md={9}
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              flexWrap: 'nowrap',
              gap: '8px',
            }}
          >
            {hasOrderExportFeature && (
              <Hidden smDown>
                <CustomButton
                  className={classes.downloadReportButton}
                  onClick={handleDownload}
                  startIcon={<SaveAltRoundedIcon />}
                >
                  Descargar Reporte
                </CustomButton>
              </Hidden>
            )}

            {hasManualOrderCreationFeature && (
              <CustomButton
                variant="outlined"
                className={classes.addOrderButton}
                startIcon={<AddCircleOutline />}
                onClick={
                  hasManualOrderCreationFeature
                    ? () => history.push('/orders/manual-order')
                    : () => setTierRestrictedModal(true)
                }
              >
                Crear pedido
              </CustomButton>
            )}

            <CustomButton
              href={TIENDADA_LINKS.tutorials.ORDERS}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.tutorialsButton}
              startIcon={<PlayCircleFilledTwoTone />}
            >
              Tutoriales
            </CustomButton>
          </Grid>
          <Hidden smDown>
            <Grid item md={6}>
              <SearchInput
                placeholder="Buscar por nombre del cliente"
                showClearButton
                startIcon={
                  <Tooltip
                    placement="bottom"
                    classes={{tooltip: classes.filterTooltip}}
                    title="Puedes buscar por nombre o apellido del cliente"
                  >
                    <InfoOutlined color="currentColor" width={24} height={24} />
                  </Tooltip>
                }
                onSubmit={handleSearchOrderSubmit}
                className={classes.searchInput}
              />
            </Grid>
          </Hidden>

          {!showPreOrders && (
            <Hidden mdUp>
              <Grid item>
                <IconButton
                  className={`${classes.mobileFiltersButton} ${
                    mobileFiltersOpen ? classes.mobileFiltersButtonActive : ''
                  }`}
                  onClick={() =>
                    canFilterOrders
                      ? setMobileFiltersOpen(!mobileFiltersOpen)
                      : setTierRestrictedModal(true)
                  }
                >
                  <TuneRounded />
                </IconButton>
              </Grid>
            </Hidden>
          )}
        </Grid>
        <Hidden smDown>{upsellSection}</Hidden>
      </div>
      <div className={classes.content}>
        {!hasOrderManagementDashboardFeature && tierRestrictedModal && (
          <div className={classes.blocker}></div>
        )}
        <div
          className={`${classes.ordersContent} ${
            !hasOrderManagementDashboardFeature && tierRestrictedModal
              ? classes.blur
              : ''
          }`}
        >
          <Hidden mdUp>{upsellSection}</Hidden>
          {/* <Hidden mdUp>
            {hasPreOrderFeature && (
              <Tabs
                value={showPreOrders ? 1 : 0}
                onChange={handleChangeTab}
                variant="fullWidth"
                indicatorColor="primary"
              >
                <Tab
                  fullWidth
                  label="Pedidos"
                  classes={{root: classes.customTab}}
                />

                <Tab
                  fullWidth
                  label="Pre Pedidos"
                  classes={{root: classes.customTab}}
                  disabled={!hasPreOrderFeature}
                />
              </Tabs>
            )}
            <div className={classes.mobileTabsDivider} />
            {!showPreOrders && canFilterOrders && (
              <Collapse
                style={{margin: '0 -16px', padding: '0 16px'}}
                in={mobileFiltersOpen}
              >
                <HorizontalFilter>
                  <HorizontalScroll>
                    {Object.keys(ORDER_STATE_STRINGS).map(state => (
                      <FilterButton
                        key={`filter-${state}`}
                        selected={
                          selectedStates && selectedStates.indexOf(state) !== -1
                        }
                        id={state}
                        handleOnClick={handleStateOptionMobile}
                        text={ORDER_STATE_STRINGS[state]}
                      />
                    ))}
                  </HorizontalScroll>
                </HorizontalFilter>
              </Collapse>
            )}
          </Hidden> */}

          <Hidden smDown>
            <div className={classes.filterWrapper}>
              <div className={classes.filterLeft}>
                <CustomButton
                  className={`${classes.filterButton} ${
                    selectedStates && selectedStates.length === 0
                      ? classes.filterButtonActive
                      : ''
                  }`}
                  onClick={() => handleStateOption('ALL')}
                  disabled={store && !canFilterOrders}
                >
                  Ver todos
                </CustomButton>
                {Object.keys(ORDER_STATE_STRINGS).map(state => (
                  <CustomButton
                    key={`filter-${state}`}
                    className={`${classes.filterButton} ${
                      selectedStates && selectedStates.indexOf(state) !== -1
                        ? classes.filterButtonActive
                        : ''
                    }`}
                    onClick={() => handleStateOption(state)}
                    disabled={store && !canFilterOrders}
                  >
                    {ORDER_STATE_STRINGS[state]}
                    <Tooltip
                      classes={{
                        arrow: classes.filterTooltipArrow,
                        tooltip: classes.filterTooltip,
                      }}
                      title={ORDER_STATE_FILTER_TOOLTIPS[state]}
                      arrow
                      // placement="left-start"
                    >
                      <InfoOutlined style={{margin: '0px -8px 0px 8px'}} />
                    </Tooltip>
                  </CustomButton>
                ))}
                {!canFilterOrders && <PremiumFeatureBadge />}

                {hasPreOrderFeature && (
                  <CustomButton
                    className={`${classes.filterButton} ${
                      selectedStates && selectedStates[0] === 'PRE_ORDERS'
                        ? classes.filterButtonActive
                        : ''
                    }`}
                    onClick={() => handleStateOption('PRE_ORDERS')}
                    disabled={store && !hasPreOrderFeature}
                  >
                    Pre Pedidos
                  </CustomButton>
                )}
              </div>
              <Fragment>{isModalDownloadOpen ? downloadModal : null}</Fragment>
            </div>
          </Hidden>

          <CustomHorizontalScroll
            classes={{
              arrowButton: classes.tableScrollArrowButton,
            }}
          >
            <div className={classes.tableScrollContainer}>
              <Hidden smDown>
                {!showPreOrders && (
                  <div className={classes.tableHeader}>
                    <Typography className={classes.headerColumn}>
                      Número de orden
                    </Typography>
                    <Typography
                      className={classes.headerColumn}
                      style={{width: '10%'}}
                    >
                      Fecha
                    </Typography>
                    <Typography
                      className={classes.headerColumn}
                      style={{width: '12%'}}
                    >
                      Canal de venta
                    </Typography>
                    <Typography
                      className={classes.headerColumn}
                      style={{width: '18%'}}
                    >
                      Colaborador
                    </Typography>
                    <Typography
                      className={classes.headerColumn}
                      style={{width: '16%'}}
                    >
                      Nombre del Cliente
                    </Typography>
                    <Typography className={classes.headerColumn}>
                      Estado de Orden
                    </Typography>
                    {/* <Typography className={classes.headerColumn}>
                  Método de pago
                </Typography> */}
                    <Typography className={classes.headerColumn}>
                      Monto
                    </Typography>
                    {hasTaxInvoiceFeature && (
                      <Typography className={classes.headerColumn}>
                        Facturación
                      </Typography>
                    )}
                    <div style={{width: 20}} />
                  </div>
                )}
              </Hidden>
              <Hidden mdUp>
                {hasPreOrderFeature && (
                  <Tabs
                    value={showPreOrders ? 1 : 0}
                    onChange={handleChangeTab}
                    variant="fullWidth"
                    indicatorColor="primary"
                  >
                    <Tab
                      fullWidth
                      label="Pedidos"
                      classes={{root: classes.customTab}}
                    />

                    <Tab
                      fullWidth
                      label="Pre Pedidos"
                      classes={{root: classes.customTab}}
                      disabled={!hasPreOrderFeature}
                    />
                  </Tabs>
                )}
                <div className={classes.mobileTabsDivider} />

                {!showPreOrders && canFilterOrders && (
                  <Collapse
                    style={{margin: '0 -16px', padding: '0 16px'}}
                    in={mobileFiltersOpen}
                  >
                    <HorizontalFilter>
                      <HorizontalScroll>
                        <FilterButton
                          selected={
                            selectedStates && selectedStates.length === 0
                          }
                          id={'ALL'}
                          handleOnClick={handleStateOptionMobile}
                          text="Ver todos"
                        />
                        {Object.keys(ORDER_STATE_STRINGS).map(state => (
                          <FilterButton
                            key={`filter-${state}`}
                            selected={
                              selectedStates &&
                              selectedStates.indexOf(state) !== -1
                            }
                            id={state}
                            handleOnClick={handleStateOptionMobile}
                            text={ORDER_STATE_STRINGS[state]}
                          />
                        ))}
                      </HorizontalScroll>
                    </HorizontalFilter>
                  </Collapse>
                )}
              </Hidden>
              {!showPreOrders ? (
                <>
                  <List className={classes.orderList}>
                    <Hidden mdUp>
                      <SearchInput
                        fullWidth
                        placeholder="Buscar por nombre del cliente"
                        startIcon={
                          <Tooltip
                            placement="bottom"
                            classes={{tooltip: classes.filterTooltip}}
                            title="Puedes buscar por nombre o apellido del cliente"
                          >
                            <InfoOutlined
                              color="currentColor"
                              width={24}
                              height={24}
                            />
                          </Tooltip>
                        }
                        onSubmit={handleSearchOrderSubmit}
                        className={classes.searchInput}
                      />
                    </Hidden>
                    {isFetching && !orders ? (
                      Array.from({length: size}, (x, i) => (
                        <ListItem key={`skelly-${i}`}>
                          <Skeleton
                            variant="rect"
                            height={mobile ? '128px' : '48px'}
                            width="100%"
                          />
                        </ListItem>
                      ))
                    ) : // ) : (orders && orders.length > 0) || !hasOrderManagementDashboardFeature ? (
                    orders && orders.length > 0 ? (
                      // (!hasOrderManagementDashboardFeature ? fakeData.data : orders).map(
                      orders
                        .filter(o => {
                          if (selectedStates.length === 0) return true;
                          if (selectedStates.includes('PRE_ORDERS'))
                            return true;
                          return selectedStates.includes(o.orderState);
                        })
                        .map((o: Order, index: number) => (
                          <div key={`orderItem-${index}`}>
                            {index === 0 ||
                            (orders &&
                              orders[index - 1] &&
                              o &&
                              !dayjs(o.createdAt).isSame(
                                orders[index - 1].createdAt,
                                'day'
                              )) ? (
                              <Hidden mdUp>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <div className={classes.sectionHeader}>
                                    {`${dayjs(o.createdAt).format(
                                      'DD/MM/YYYY'
                                    )} -`}
                                  </div>
                                  <div className={classes.sectionHeader}>
                                    {`\u00A0${dayjs(o.createdAt).fromNow()}`}
                                  </div>
                                </div>
                              </Hidden>
                            ) : null}

                            <ListItem className={classes.listItem} button>
                              <OrderItem
                                order={o}
                                tempCollaborators={tempCollaborators}
                                fetchCollaboratorInfo={fetchCollaboratorInfo}
                                setTierRestrictedModal={setTierRestrictedModal}
                                stockSettings={stockSettings}
                              />
                            </ListItem>
                            <Hidden smDown>
                              <Divider className={classes.divider} />
                            </Hidden>
                          </div>
                        ))
                    ) : (
                      <Typography className={classes.noOrders}>
                        No se encontraron órdenes
                      </Typography>
                    )}
                  </List>
                  {paginationArea}
                </>
              ) : (
                <>
                  <List className={classes.orderList}>
                    {isFetching && !orders ? (
                      Array.from({length: size}, (x, i) => (
                        <ListItem key={`skelly-${i}`}>
                          <Skeleton
                            variant="rect"
                            height={mobile ? '128px' : '48px'}
                            width="100%"
                          />
                        </ListItem>
                      ))
                    ) : // ) : (orders && orders.length > 0) || !hasOrderManagementDashboardFeature ? (
                    orders && orders.length > 0 ? (
                      <>
                        <Hidden smDown>
                          <div className={classes.tableHeader}>
                            <Typography className={classes.headerColumn}>
                              Número de pedido
                            </Typography>
                            <Typography className={classes.headerColumn}>
                              Fecha
                            </Typography>
                            <Typography className={classes.headerColumn}>
                              Sub total
                            </Typography>
                            <Typography className={classes.headerColumn}>
                              # de productos
                            </Typography>
                            <div className={classes.headerColumn}>
                              <div style={{width: 268}} />
                            </div>
                            <div style={{width: 68}} />
                          </div>
                        </Hidden>
                        {// (!hasOrderManagementDashboardFeature ? fakeData.data : orders).map(
                        orders
                          .filter(o => {
                            if (selectedStates.length === 0) return true;
                            if (selectedStates.includes('PRE_ORDERS'))
                              return true;
                            return selectedStates.includes(o.orderState);
                          })
                          .map((o: Order, index: number) => (
                            <div key={`orderItem-${index}`}>
                              {index === 0 ||
                              (orders &&
                                orders[index - 1] &&
                                o &&
                                !dayjs(o.createdAt).isSame(
                                  orders[index - 1].createdAt,
                                  'day'
                                )) ? (
                                <Hidden mdUp>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <div className={classes.sectionHeader}>
                                      {`${dayjs(o.createdAt).format(
                                        'DD/MM/YYYY'
                                      )} -`}
                                    </div>
                                    <div className={classes.sectionHeader}>
                                      {`\u00A0${dayjs(o.createdAt).fromNow()}`}
                                    </div>
                                  </div>
                                </Hidden>
                              ) : null}
                              <ListItem className={classes.listItem} button>
                                <OrderItem
                                  order={o}
                                  tempCollaborators={tempCollaborators}
                                  fetchCollaboratorInfo={fetchCollaboratorInfo}
                                  setTierRestrictedModal={
                                    setTierRestrictedModal
                                  }
                                  stockSettings={stockSettings}
                                />
                              </ListItem>
                              <Hidden smDown>
                                <Divider className={classes.divider} />
                              </Hidden>
                            </div>
                          ))}
                      </>
                    ) : (
                      <div className={classes.noPreOrdersContainer}>
                        <Typography className={classes.noPreOrdersTitle}>
                          ¿Qué es un pre pedido?
                        </Typography>
                        <Typography className={classes.noPreOrdersText}>
                          Es un carrito de compras digital con productos
                          específicos que tú puedes seleccionar.
                        </Typography>
                        <Typography className={classes.noPreOrdersTitle}>
                          ¿Cómo se usa?
                        </Typography>
                        <img
                          src={mobile ? preOrderSteps : preOrderSteps2}
                          alt=""
                          className={classes.noPreOrdersText}
                        />
                        <Typography className={classes.noPreOrdersText}>
                          Y tú, ahorras tiempo registrando los datos de tu
                          cliente mientras mantienes tus inventarios, pedidos e
                          información ordenada y en un sólo lugar.
                        </Typography>
                      </div>
                    )}
                  </List>
                  {paginationArea}
                </>
              )}
            </div>
          </CustomHorizontalScroll>
          <StateChangeInfoModal
            open={tutorialModalOpen}
            handleClose={() => setTutorialModalOpen(false)}
          />
          {/* {!hasOrderManagementDashboardFeature && !tierRestrictedModal && (!mobile || mobile && ) (
          <div className={classes.upsellMessage2}>
            <InfoOutlined />
            <Typography className={classes.upsellText}>
              Recuerda que la versión gratis envía los pedidos recibidos a tu
              WhatsApp a través de un mensaje automatizado que incluye toda la
              información de la compra.
            </Typography>
          </div>
        )} */}
        </div>
      </div>
    </div>
  );
}

Orders.propTypes = {
  fetchOrders: PropTypes.func.isRequired,
  fetchPreOrders: PropTypes.func.isRequired,
  orders: PropTypes.array,
  pagination: PropTypes.object,
  queryFilter: PropTypes.object,
  isFetching: PropTypes.bool,
  store: PropTypes.object,
  currency: PropTypes.object,
  selectedStates: PropTypes.array,
  toggleSelectedState: PropTypes.func.isRequired,
  updateSelectedStates: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  resetFilters: PropTypes.func.isRequired,
  updateOrderStateSuccess: PropTypes.object,
  updateOrderStateError: PropTypes.object,
  orderStateMessages: PropTypes.array,
  getOrderStateMessages: PropTypes.func.isRequired,
  resetOrderState: PropTypes.func.isRequired,
  fetchCollaborators: PropTypes.func.isRequired,
  fetchCollaboratorInfo: PropTypes.func.isRequired,
  collaborators: PropTypes.array,
  updateOrderCollaboratorSuccess: PropTypes.bool,
  tempCollaborators: PropTypes.object.isRequired,
};
